import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Pagination,
  CircularProgress,
  Button,
  alpha,
  useTheme,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Components
import PDFListItem from './PDFListItem';

// Stores
import usePDFStore from '../../stores/pdfStore';

const PDFList = ({ viewMode = 'grid', onViewModeChange }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    pdfs,
    filters,
    categories,
    loading,
    pagination,
    fetchPDFs,
    fetchCategories,
    setFilters,
    clearFilters
  } = usePDFStore();
  
  const [searchValue, setSearchValue] = useState(filters.search || '');
  
  // Load categories on mount
  useEffect(() => {
    // Fetch categories when component mounts
    // The error handling in the store will prevent auth errors from showing
    const loadCategories = async () => {
      try {
        await fetchCategories();
      } catch (error) {
        // Error already handled in store
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, [fetchCategories]);
  
  // Handle search change
  const handleSearchChange = (value) => {
    setSearchValue(value);
    setFilters({ search: value, page: 1 });
    fetchPDFs();
  };
  
  // Handle clear search
  const handleClearSearch = () => {
    setSearchValue('');
    setFilters({ search: '', page: 1 });
    fetchPDFs();
  };
  
  // Handle filter change
  const handleFilterChange = async (filterName, value) => {
    try {
      setFilters({ [filterName]: value, page: 1 });
      await fetchPDFs();
    } catch (error) {
      // Error already handled in store
      console.error('Error applying filter:', error);
    }
  };
  
  // Handle clear all filters
  const handleClearAllFilters = async () => {
    try {
      clearFilters();
      setSearchValue('');
      await fetchPDFs();
    } catch (error) {
      // Error already handled in store
      console.error('Error clearing filters:', error);
    }
  };
  
  // Handle sort change
  const handleSortChange = (field, direction) => {
    setFilters({ sortBy: field, sortDirection: direction, page: 1 });
    fetchPDFs();
  };
  
  // Handle page change
  const handlePageChange = async (newPage) => {
    try {
      setFilters({ page: newPage });
      await fetchPDFs();
    } catch (error) {
      // Error already handled in store
      console.error('Error changing page:', error);
    }
  };
  
  // Build active filters array for display
  const activeFilters = [];
  if (filters.status && filters.status !== 'all') {
    activeFilters.push({
      label: `Status: ${filters.status}`,
      onDelete: () => handleFilterChange('status', 'all')
    });
  }
  if (filters.category && filters.category !== 'all') {
    const categoryName = categories.find(c => c._id === filters.category)?.name || filters.category;
    activeFilters.push({
      label: `Category: ${categoryName}`,
      onDelete: () => handleFilterChange('category', 'all')
    });
  }
  
  // Filter configuration
  const filterConfig = [
    {
      name: 'status',
      label: 'Status',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'published', label: 'Published' },
        { value: 'expired', label: 'Expired' },
        { value: 'archived', label: 'Archived' }
      ],
      allLabel: 'All Statuses'
    },
    {
      name: 'category',
      label: 'Category',
      options: categories.map(cat => ({ value: cat._id, label: cat.name })),
      allLabel: 'All Categories'
    }
  ];
  
  // Sort options
  const sortOptions = [
    { field: 'createdAt', label: 'Created Date' },
    { field: 'updatedAt', label: 'Updated Date' },
    { field: 'title', label: 'Title' },
    { field: 'status', label: 'Status' }
  ];
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Active Filters Display */}
      {activeFilters.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {activeFilters.map((filter, index) => (
            <Chip
              key={index}
              label={filter.label}
              size="small"
              onDelete={filter.onDelete}
              sx={{ borderRadius: 1 }}
            />
          ))}
          <Button
            size="small"
            onClick={handleClearAllFilters}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Clear all
          </Button>
        </Box>
      )}
      
      {/* PDF List/Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flex: 1 }}>
          <CircularProgress size={32} />
        </Box>
      ) : pdfs.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          py: 8
        }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No PDFs found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {(activeFilters.length > 0 || searchValue) 
              ? 'Try adjusting your filters or search criteria'
              : 'Create your first PDF document to get started'
            }
          </Typography>
          {activeFilters.length === 0 && !searchValue && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/pdf/create')}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Create Your First PDF
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Grid container spacing={viewMode === 'grid' ? 2 : 0}>
            {pdfs.map(pdf => (
              <Grid 
                item 
                key={pdf._id} 
                xs={12}
                sm={viewMode === 'grid' ? 6 : 12}
                md={viewMode === 'grid' ? 4 : 12}
                lg={viewMode === 'grid' ? 3 : 12}
              >
                <PDFListItem 
                  pdf={pdf} 
                  viewMode={viewMode}
                />
              </Grid>
            ))}
          </Grid>
          
          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 3,
              pb: 2
            }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={(e, value) => handlePageChange(value)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PDFList;